import {StringSchema} from 'joi';
import pickBy from 'lodash/pickBy';

import {EU_COUNTRIES} from 'src/containers/LeadCompanyClients/definitions';

import {
  EDetailsAction,
  EEntityType,
  EResidentCountry,
  INDIVIDUAL_TAX_NUMBER_LENGTH,
  LEGAL_ENTITY_TAX_NUMBER_LENGTH,
  TCompanyDetails,
} from './definitions';

export const getResidentCountry = (country?: TCompanyDetails['country']): EResidentCountry => {
  if (!country) {
    return EResidentCountry.RUS;
  }

  if (Object.values(EResidentCountry).includes(country as EResidentCountry)) {
    return country as EResidentCountry;
  }

  return EResidentCountry.FOREIGN;
};

export const getActionName = (details?: TCompanyDetails) => {
  if (!details) {
    return EDetailsAction.FILL;
  }

  const emptyFieldsCount = Object.keys(
    pickBy(details, (value) => value === undefined || value === null),
  ).length;

  if (emptyFieldsCount === 0) {
    return EDetailsAction.UPDATE;
  }

  if (Object.keys(details).length === emptyFieldsCount) {
    return EDetailsAction.FILL;
  }

  return EDetailsAction.ADD;
};

export const getCompanyDetailsSchema =
  (
    residentCountry: EResidentCountry,
    {
      hasOgrn,
      hasTaxNumber,
      countryCode,
    }: {hasOgrn: boolean; hasTaxNumber: boolean; countryCode?: string},
  ): TCreateValidateSchema =>
  (joi, mixed) => {
    const common: Partial<Record<keyof TCompanyDetails, StringSchema>> = {
      taxNumber: joi.string().trim().required(),
      companyName: joi.string().trim().required(),
      legalAddress: joi.string().trim().required(),
    };

    switch (residentCountry) {
      case EResidentCountry.RUS:
        const rus = {
          ...common,
          taxNumber: mixed.tin.required(),
          siteAddress: joi.string(),
          kpp: mixed.kpp.when('taxNumber', {
            is: joi.string().length(LEGAL_ENTITY_TAX_NUMBER_LENGTH),
            then: joi.required(),
          }),
          postAddress: joi.string().required(),
        };

        if (hasOgrn) {
          rus.ogrn = joi
            .string()
            .length(13)
            .required()
            .when('taxNumber', {
              is: joi.string().min(12).exist(),
              then: joi.string().length(15).required(),
            });
        }

        if (!hasTaxNumber) {
          rus.entityType = joi.string().required();
          rus.taxNumber = joi
            .string()
            .required()
            .when('entityType', {
              is: EEntityType.INDIVIDUAL,
              then: joi.string().length(INDIVIDUAL_TAX_NUMBER_LENGTH).required(),
              otherwise: joi.string().length(LEGAL_ENTITY_TAX_NUMBER_LENGTH).required(),
            });
        }

        return joi.object(rus);

      case EResidentCountry.BLR:
        return joi.object({
          ...common,
          siteAddress: joi.string(),
          postAddress: joi.string().required(),
        });
      case EResidentCountry.KAZ:
        return joi.object({
          ...common,
          postAddress: joi.string().required(),
        });
      case EResidentCountry.FOREIGN:
        const foreign = {...common};

        if (countryCode && EU_COUNTRIES.includes(countryCode)) {
          foreign.taxNumber = mixed.vatNumber.required();
        }

        if (hasOgrn) {
          foreign.ogrn = joi.string().max(100);
        }

        return joi.object(foreign);
    }
  };
